import { Badge, Box, Button, CircularProgress, IconButton, Popover, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEdition } from '@/hooks/useEdition'
import { dashboardService, type DashboardActivityEvent } from '@/services/dashboardService'
import { activityMeta } from './activityMeta'

/** localStorage key holding the timestamp (ms) of the last time the bell was opened. */
const SEEN_AT_STORAGE_KEY = 'facewise_notifications_seen_at'
/** How far back the bell looks for events. */
const FEED_DAYS = 7
/** Background refresh cadence. */
const POLL_MS = 60_000
/** Max rows shown in the dropdown — the Activity tab has the full feed. */
const MAX_ITEMS = 8

function readSeenAt(): number {
  const raw = localStorage.getItem(SEEN_AT_STORAGE_KEY)
  const parsed = raw ? Number(raw) : NaN
  return Number.isFinite(parsed) ? parsed : 0
}

function useRelativeTime() {
  const { i18n } = useTranslation()
  const rtf = useMemo(() => new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }), [i18n.language])
  return useCallback(
    (at: string) => {
      const minutesAgo = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60_000))
      if (minutesAgo < 60) return rtf.format(-minutesAgo, 'minute')
      if (minutesAgo < 60 * 24) return rtf.format(-Math.round(minutesAgo / 60), 'hour')
      return rtf.format(-Math.round(minutesAgo / (60 * 24)), 'day')
    },
    [rtf],
  )
}

type NotificationsBellProps = {
  /** Shared icon-button styling from the top bar. */
  iconBtnSx?: SxProps<Theme>
}

/**
 * Top-bar notification bell backed by the researcher activity feed.
 *
 * Unread = events newer than the locally persisted "seen at" timestamp;
 * opening the dropdown marks everything as seen (badge clears) while the
 * rows that were unread stay highlighted until the popover closes.
 */
export default function NotificationsBell({ iconBtnSx }: NotificationsBellProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const formatRelative = useRelativeTime()
  const { isPro } = useEdition()

  // undefined = loading, null = failed
  const [events, setEvents] = useState<DashboardActivityEvent[] | null | undefined>(undefined)
  const [seenAt, setSeenAt] = useState<number>(() => readSeenAt())
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // Snapshot of unread event ids taken when the popover opens, so rows keep
  // their "new" highlight even though the badge clears immediately.
  const [freshIds, setFreshIds] = useState<Set<string>>(() => new Set())
  const eventsRef = useRef(events)
  eventsRef.current = events

  const refresh = useCallback(() => {
    dashboardService
      .getActivity(FEED_DAYS)
      .then((data) => setEvents(data.events))
      .catch(() => setEvents((prev) => prev ?? null))
  }, [])

  useEffect(() => {
    refresh()
    const id = window.setInterval(refresh, POLL_MS)
    return () => window.clearInterval(id)
  }, [refresh])

  const unreadCount = useMemo(
    () => (events ?? []).filter((e) => new Date(e.at).getTime() > seenAt).length,
    [events, seenAt],
  )

  const visibleEvents = useMemo(() => (events ?? []).slice(0, MAX_ITEMS), [events])

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    refresh()
    const current = eventsRef.current ?? []
    setFreshIds(new Set(current.filter((ev) => new Date(ev.at).getTime() > seenAt).map((ev) => ev.id)))
    const now = Date.now()
    localStorage.setItem(SEEN_AT_STORAGE_KEY, String(now))
    setSeenAt(now)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setFreshIds(new Set())
  }

  const eventTarget = (e: DashboardActivityEvent) =>
    e.kind === 'reachedRespondents' && e.target_n != null ? t('recentActivity.goal', { n: e.target_n }) : e.target

  const meta = activityMeta(c)
  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton aria-label={t('topbar.notifications')} onClick={handleOpen} sx={iconBtnSx}>
        <Badge
          badgeContent={unreadCount}
          max={9}
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: c.coral,
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              px: '4px',
              top: -2,
              right: -2,
              border: `2px solid ${c.white}`,
            },
          }}
        >
          <NotificationsNoneIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: '14px',
              border: `1px solid ${c.ink08}`,
              boxShadow: '0 12px 32px rgba(27,28,56,.12)',
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${c.ink08}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: c.ink }}>
            {t('notificationsBell.title')}
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
          {events === undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {events !== undefined && visibleEvents.length === 0 && (
            <Typography sx={{ fontSize: 13, color: c.ink56, textAlign: 'center', py: 4, px: 2 }}>
              {events === null ? t('notificationsBell.loadFailed') : t('notificationsBell.empty')}
            </Typography>
          )}

          {visibleEvents.map((e, i) => {
            const { Icon, color } = meta[e.kind]
            const target = eventTarget(e)
            const fresh = freshIds.has(e.id)
            return (
              <Box
                key={e.id}
                onClick={() => {
                  if (e.project_id) {
                    handleClose()
                    navigate(`/studies/${e.project_id}`)
                  }
                }}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  px: 2,
                  py: 1.25,
                  bgcolor: fresh ? c.coralSoft : 'transparent',
                  borderBottom: i < visibleEvents.length - 1 ? `1px dashed ${c.ink08}` : 'none',
                  minWidth: 0,
                  cursor: e.project_id ? 'pointer' : 'default',
                  transition: 'background-color 120ms',
                  '&:hover': e.project_id ? { bgcolor: fresh ? c.coralSoft : c.gray50 } : undefined,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: `${theme.affexy.radii.sm + 1}px`,
                    bgcolor: fresh ? c.white : c.gray50,
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 28px',
                    color,
                  }}
                >
                  <Icon sx={{ fontSize: 15 }} />
                </Box>
                <Typography sx={{ flex: 1, fontSize: 13, lineHeight: 1.4, minWidth: 0 }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {e.who}
                  </Box>{' '}
                  <Box component="span" sx={{ color: c.ink72 }}>
                    {t(`recentActivity.verb.${e.kind}`, { count: e.count ?? 0 })}
                  </Box>
                  {target && (
                    <>
                      {' '}
                      <Box component="span" sx={{ color: c.coral, fontWeight: 500 }}>
                        {target}
                      </Box>
                    </>
                  )}
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: c.ink48, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                >
                  {formatRelative(e.at)}
                </Typography>
              </Box>
            )
          })}
        </Box>

        {/* The full feed is a Pro room; offering it to FaceWise would only
            bounce the reader back to the dashboard. */}
        {isPro && (
          <Box sx={{ borderTop: `1px solid ${c.ink08}`, p: 0.75 }}>
            <Button
              fullWidth
              color="inherit"
              sx={{ color: c.ink72, fontSize: 13, borderRadius: '10px' }}
              onClick={() => {
                handleClose()
                navigate('/activity')
              }}
            >
              {t('notificationsBell.showAll')}
            </Button>
          </Box>
        )}
      </Popover>
    </>
  )
}
